import React from 'react'
import { Formik, Form, Field } from 'formik'
import { Button, Columns, Element } from 'react-bulma-components'
import InputField from '../form/fields/input'
import SelectField from '../form/fields/select'
import AddressAutocompleteField from '../form/fields/address-autocomplete-field'
import {
  countriesAsSelectioOptions,
  phonePrefixesAsSelectionOptions,
} from '../../constants/countries'
import { Translate, translate } from '../../utils/translations'
import { RepairKitRequestFormValues } from './repair-kit-form-shared'

export type { RepairKitRequestFormValues } from './repair-kit-form-shared'

interface RepairKitRequestFormProps {
  initialEmail?: string
  initialCountry?: string
  onSuccess: (values: RepairKitRequestFormValues) => void | Promise<void>
  language: string
  /** Raw values entered on a previous visit, used to pre-fill the form. */
  savedValues?: Partial<RepairKitRequestFormValues>
  /** Persists the raw form values for back/forward navigation. */
  onSaveValues?: (values: RepairKitRequestFormValues) => void
  /** Disables the submit button while an async onSuccess handler runs. */
  isSubmitting?: boolean
}

const initialFormValues = (email = '', country = ''): RepairKitRequestFormValues => ({
  email,
  firstName: '',
  lastName: '',
  address: '',
  streetAddress: '',
  additionalAddress: '',
  zip: '',
  city: '',
  country: country || 'FR',
  phonePrefix: '',
  phone: '',
  addressComplete: false,
  readConfirmation: false,
})

const validate = (values: RepairKitRequestFormValues) => {
  const errors: Record<string, React.ReactNode> = {}
  if (!values.email) {
    errors.email = <Translate value="fields.required" />
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
    errors.email = <Translate value="fields.validations.email" />
  }
  if (!values.firstName) errors.firstName = <Translate value="fields.required" />
  if (!values.lastName) errors.lastName = <Translate value="fields.required" />
  if (!values.address) {
    errors.address = translate('fields.required')
  } else if (!values.zip || !values.city) {
    errors.address = translate('fields.validations.selectAddress')
  } else {
    const streetLine = (values.streetAddress || values.address).trim()
    const hasStreetNumber = values.addressComplete === true || /^\d+\s/.test(streetLine)
    if (!hasStreetNumber) {
      errors.address = translate('fields.validations.incompleteAddress')
    }
  }
  if (!values.country) errors.country = <Translate value="fields.required" />
  if (!values.phonePrefix) errors.phonePrefix = <Translate value="fields.required" />
  if (!values.phone) errors.phone = <Translate value="fields.required" />

  return errors
}

const RepairKitRequestForm: React.FC<RepairKitRequestFormProps> = ({
  initialEmail = '',
  initialCountry = '',
  onSuccess,
  language,
  savedValues,
  onSaveValues,
  isSubmitting = false,
}) => {
  const formInitialValues: RepairKitRequestFormValues = {
    ...initialFormValues(initialEmail, initialCountry),
    ...(savedValues ?? {}),
    email: (initialEmail || savedValues?.email) ?? '',
    country: (initialCountry || savedValues?.country) ?? 'FR',
    readConfirmation: false,
  }

  const handleFormSubmit = async (
    values: RepairKitRequestFormValues & { streetNumber?: string },
  ) => {
    delete values.streetNumber
    onSaveValues?.(values)
    await onSuccess(values)
  }

  return (
    <Formik
      enableReinitialize
      initialValues={formInitialValues}
      validate={validate}
      onSubmit={handleFormSubmit}
    >
      {() => (
        <Form>
          <Field
            name="email"
            component={InputField}
            type="email"
            label={<Translate value="fields.email" />}
            disabled={!!initialEmail}
          />
          <Field
            name="firstName"
            component={InputField}
            type="text"
            label={<Translate value="fields.lastName" />}
          />
          <Field
            name="lastName"
            component={InputField}
            type="text"
            label={<Translate value="fields.firstName" />}
          />
          <Field
            name="country"
            component={SelectField}
            label={<Translate value="fields.country" />}
            options={countriesAsSelectioOptions}
            selectDefaultChooseOptionLabel={<Translate value="fields.choose" />}
            disabled
          />
          <Field
            name="address"
            component={AddressAutocompleteField}
            countryCode={initialCountry || formInitialValues.country}
            language={language}
            label={<Translate value="fields.deliveryAddress" />}
            help={translate('fields.deliveryAddressHelp')}
          />
          <Field
            name="additionalAddress"
            component={InputField}
            type="text"
            autoComplete="off"
            readOnly
            onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
              event.currentTarget.readOnly = false
            }}
            label={<Translate value="fields.additionalAddress" />}
          />
          <Field type="hidden" name="streetAddress" />
          <Field type="hidden" name="zip" />
          <Field type="hidden" name="city" />
          <Field type="hidden" name="addressComplete" />
          <Columns>
            <Columns.Column size={4}>
              <Field
                name="phonePrefix"
                component={SelectField}
                label={<Translate value="fields.phonePrefix" />}
                options={phonePrefixesAsSelectionOptions}
                selectDefaultChooseOptionLabel={<Translate value="fields.choose" />}
              />
            </Columns.Column>
            <Columns.Column size={8}>
              <Field
                name="phone"
                component={InputField}
                type="tel"
                label={<Translate value="fields.phone" />}
              />
            </Columns.Column>
          </Columns>
          <Element mt={4} textAlign="center">
            <Button color="primary" type="submit" disabled={isSubmitting} data-testid="step-submit">
              <Translate value="form.submit" />
            </Button>
          </Element>
        </Form>
      )}
    </Formik>
  )
}

export default RepairKitRequestForm
